import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

import { useLocalization } from '@/shared/composables/localization'

import { useQueueServiceApi } from '../api'
import { isCallQueuesSettingsVisible } from '../model'
import { QueueService } from '../types'

const syncSettingsVisibility = (queues: QueueService[]) => {
  isCallQueuesSettingsVisible.value = queues.length > 0
}

export const useQueueServiceStore = defineStore('queue-service', () => {
  const { t } = useLocalization()
  const api = useQueueServiceApi()

  const queues = ref<QueueService[]>([])
  const isLoading = ref(false)
  const isSaving = ref(false)
  const savingAgentGuids = ref<Set<string>>(new Set())

  const hasQueues = computed(() => queues.value.length > 0)

  const applyQueues = (data: QueueService[]) => {
    queues.value = data
    syncSettingsVisibility(data)
  }

  const mergePatchedQueues = (patched: QueueService[]) => {
    const next = [...queues.value]

    for (const item of patched) {
      const index = next.findIndex((queue) => queue.agentGuid === item.agentGuid)

      if (index >= 0) {
        next[index] = item
      }
    }

    applyQueues(next)
  }

  const fetchAll = async (): Promise<QueueService[]> => {
    if (isLoading.value) {
      return queues.value
    }

    try {
      isLoading.value = true
      const { data = [] } = await api.getQueueServices()
      applyQueues(data)
      return data
    } catch (e) {
      console.error(e)
      applyQueues([])
      return []
    } finally {
      isLoading.value = false
    }
  }

  const updateQueues = async (payload: QueueService[]): Promise<void> => {
    if (!payload.length) {
      return
    }

    const guids = payload.map((item) => item.agentGuid)
    guids.forEach((guid) => savingAgentGuids.value.add(guid))

    try {
      isSaving.value = true
      const { data } = await api.patchQueueServices(payload)

      if (Array.isArray(data) && data.length) {
        applyQueues(data)
      } else {
        mergePatchedQueues(payload)
      }
    } catch (e) {
      throw new Error(t('common.failedToChangeElement', { e }))
    } finally {
      isSaving.value = false
      guids.forEach((guid) => savingAgentGuids.value.delete(guid))
    }
  }

  const updateQueueEnabled = async (agentGuid: string, enabled: boolean): Promise<void> => {
    const queue = queues.value.find((item) => item.agentGuid === agentGuid)

    if (!queue) {
      return
    }

    await updateQueues([{ ...queue, enabled }])
  }

  const disableAllQueues = async (): Promise<void> => {
    if (!queues.value.length) {
      return
    }

    await updateQueues(queues.value.map((queue) => ({ ...queue, enabled: false })))
  }

  const enableAllQueues = async (): Promise<void> => {
    const payload = queues.value
      .filter((queue) => !queue.enabled)
      .map((queue) => ({ ...queue, enabled: true }))

    if (!payload.length) {
      return
    }

    await updateQueues(payload)
  }

  const hasDisabledQueues = computed(() => queues.value.some((queue) => !queue.enabled))
  const hasEnabledQueues = computed(() => queues.value.some((queue) => queue.enabled))

  const isAgentSaving = (agentGuid: string) => savingAgentGuids.value.has(agentGuid)

  return {
    queues,
    isLoading,
    isSaving,
    hasQueues,
    hasDisabledQueues,
    hasEnabledQueues,
    fetchAll,
    updateQueues,
    updateQueueEnabled,
    disableAllQueues,
    enableAllQueues,
    isAgentSaving,
  }
})
